<template>
    <div class="card-box">
        <div class="card-title">{{ card.name || '未命名卡片' }}</div>
        <div class="card-body">
            <div ref="chartRef" class="chart-container"></div>
            <div v-if="loading" class="loading-overlay">
                <el-icon class="is-loading"><Loading /></el-icon>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import { Loading } from '@element-plus/icons';
import request from '@/utils/request';

export default {
    name: 'CardChartLine',
    components: { Loading },
    props: {
        card: { type: Object, required: true },
        viewId: { type: String, default: '' },
        sectionIndex: { type: Number, default: -1 },
        cardIndex: { type: Number, default: -1 }
    },
    data() {
        return {
            loading: false
        };
    },
    created() {
        this.chartInstance = null;
    },
    mounted() {
        this.initChart();
        this.fetchData();
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
    },
    methods: {
        initChart() {
            if (this.$refs.chartRef && !this.chartInstance) {
                this.chartInstance = echarts.init(this.$refs.chartRef);
            }
        },
        transformToChartData(rawData) {
            const params = this.card.params || {};
            const categoriesField = params.categories || 'create_time';
            const seriesNameField = params.seriesName || 'cust_name';
            const seriesDataField = params.seriesData || 'cpu_use';

            const result = { categories: [], series: [] };
            if (!rawData || rawData.length === 0) {
                return result;
            }

            const seriesMap = new Map();
            const pointMap = new Map();

            // 第一步：收集 categories（保持顺序）并初始化 seriesMap
            for (const item of rawData) {
                const cat = item[categoriesField];
                const name = item[seriesNameField];

                if (cat !== undefined && cat !== null && !result.categories.includes(cat)) {
                    result.categories.push(cat);
                }
                if (name !== undefined && name !== null && !seriesMap.has(name)) {
                    seriesMap.set(name, []);
                }
            }

            // 第二步：缓存所有数据点，便于快速查找
            for (const item of rawData) {
                const name = item[seriesNameField];
                const cat = item[categoriesField];
                pointMap.set(name + '-' + cat, Number(item[seriesDataField]));
            }

            // 第三步：填充每个 series 的数据
            seriesMap.forEach((val, name) => {
                const data = result.categories.map(cat => {
                    const v = pointMap.get(name + '-' + cat);
                    return v !== undefined ? v : 0;
                });
                result.series.push({ name, data });
            });

            return result;
        },
        updateChart(chartData) {
            if (!this.chartInstance) {
                return;
            }

            // 根据最大值自动计算左侧留白宽度
            let maxValue = 0;
            chartData.series.forEach(s => s.data.forEach(v => {
                if (v > maxValue) maxValue = v;
            }));
            const maxDigits = String(Math.floor(maxValue)).length;
            const gridLeft = Math.max(15, maxDigits * 10);

            const options = {
                color: ['#1890FF', '#91CB74', '#FAC858', '#EE6666', '#73C0DE', '#3CA272', '#FC8452', '#9A60B4', '#EA7CCC'],
                tooltip: { show: true, trigger: 'axis' },
                legend: { show: true, bottom: 0 },
                grid: { left: gridLeft, right: 15, top: 30, bottom: 40 },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: chartData.categories,
                    axisLine: { lineStyle: { color: '#E0E0E0' } },
                    axisLabel: { color: '#999' }
                },
                yAxis: {
                    type: 'value',
                    splitLine: { lineStyle: { type: 'dashed', color: '#E0E0E0' } },
                    axisLabel: { color: '#999' }
                },
                series: chartData.series.map(item => ({
                    type: 'line',
                    smooth: true,
                    name: item.name,
                    data: item.data,
                    lineStyle: { width: 2 },
                    areaStyle: { opacity: 0.1 }
                }))
            };

            this.chartInstance.setOption(options);
        },
        async fetchData() {
            if (!this.viewId || this.sectionIndex < 0 || this.cardIndex < 0) {
                return;
            }

            this.loading = true;
            try {
                const res = await request({
                    url: '/dataview/getCardData',
                    method: 'post',
                    data: {
                        id: this.viewId,
                        sectionIndex: this.sectionIndex,
                        cardIndex: this.cardIndex
                    }
                });

                let rawData = [];
                if (res && res.code === 0 && res.meta && res.meta.data) {
                    rawData = res.meta.data;
                }

                const chartData = this.transformToChartData(rawData);
                if (!this.chartInstance) {
                    this.initChart();
                }
                this.updateChart(chartData);
            } catch (e) {
                console.error('获取折线图数据失败:', e);
            } finally {
                this.loading = false;
            }
        },
        handleResize() {
            if (this.chartInstance) {
                this.chartInstance.resize();
            }
        }
    }
};
</script>

<style scoped>
.card-box {
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}
.card-title {
    padding: 10px 14px;
    font-size: 14px;
    font-weight: bold;
    border-bottom: 1px solid #f0f2f5;
    background: #fafafa;
    color: #303133;
}
.card-body {
    position: relative;
    height: 240px;
    padding: 8px;
}
.chart-container {
    width: 100%;
    height: 100%;
}
.loading-overlay {
    position: absolute;
    top: 8px;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    font-size: 24px;
    color: #cdaf93;
}
</style>
